import React, { useState } from 'react';
import { FaClock, FaCheckCircle, FaSpinner, FaTimes, FaStar, FaUsers, FaChartLine } from 'react-icons/fa';
import axios from 'axios';
import {
  BookOpen,
  Briefcase,
  TrendingUp,
  Wallet,
  Link,
  DollarSign
} from "lucide-react";

import EduCoverImg from '../../Assets/Financial-Courses-Wealth-Management.png'

const CoursesPage = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course_id: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Courses data
  const courses = [
    {
      id: 1,
      title: 'Career Guide To Financial Markets',
      description: 'Understand the structure of financial markets, career opportunities, and the skills required to succeed.',
      duration: '6 weeks',
      level: 'Beginner',
      category: 'finance',
      image: EduCoverImg,
      rating: 4.8,
    },
    {
      id: 2,
      title: 'Technical And Fundamental Analysis on Stock Market',
      description: 'Master chart patterns, indicators, and fundamental valuation techniques for informed trading decisions.',
      duration: '10 weeks',
      level: 'Intermediate',
      category: 'stock-market',
      image: EduCoverImg,
      rating: 4.9,
    },
    {
      id: 3,
      title: 'Basic Course on Mutual Funds and Wealth Management',
      description: 'Learn about mutual funds, portfolio management, and strategies for long-term wealth creation.',
      duration: '8 weeks',
      level: 'Beginner',
      category: 'wealth',
      image: EduCoverImg,
      rating: 4.7,
     
    },
    {
      id: 4,
      title: 'Basic Course on Crypto Currency And Block Chain Technology',
      description: 'Get started with cryptocurrency, blockchain, and decentralized finance (DeFi).',
      duration: '6 weeks',
      level: 'Beginner to Intermediate',
      category: 'crypto',
      image: EduCoverImg,
      rating: 4.6,
      
    },
    {
      id: 5,
      title: 'Basic Course on Forex Trading',
      description: 'Understand the forex market, currency pairs, and trading strategies to enter global markets.',
      duration: '8 weeks',
      level: 'Beginner',
      category: 'forex',
      image: EduCoverImg,
      rating: 4.5,
      
    }
  ];

  const categories = [
    { id: "all", name: "All Courses", icon: BookOpen, count: courses.length },
    { id: "finance", name: "Financial Markets", icon: Briefcase, count: courses.filter(c => c.category === 'finance').length },
    { id: "stock-market", name: "Stock Market", icon: TrendingUp, count: courses.filter(c => c.category === 'stock-market').length },
    { id: "wealth", name: "Mutual Funds & Wealth", icon: Wallet, count: courses.filter(c => c.category === 'wealth').length },
    { id: "crypto", name: "Crypto & Blockchain", icon: Link, count: courses.filter(c => c.category === 'crypto').length },
    { id: "forex", name: "Forex Trading", icon: DollarSign, count: courses.filter(c => c.category === 'forex').length },
  ];

  const filteredCourses = activeCategory === 'all'
    ? courses
    : courses.filter(course => course.category === activeCategory);

  const handleEnrollClick = (course) => {
    setSelectedCourse(course);
    setFormData({
      name: '',
      email: '',
      phone: '',
      course_id: course.id,
      message: ''
    });
    setShowModal(true);
    setSubmitError('');
    setErrors({});
    setIsSubmitted(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsSubmitted(false);
    setErrors({});
    setSubmitError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
    
    if (submitError) {
      setSubmitError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');

    try {
      console.log('Submitting form data:', formData);
      
      // Try to submit to the actual API
      const response = await axios.post(
        'https://banglacalender.in/api/course_enrolment',
        formData,
        { 
          headers: { 'Content-Type': 'application/json' },
          timeout: 10000
        }
      );

      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      
      if (response.status === 200 || response.status === 201) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          course_id: '',
          message: ''
        });
      } else {
        setSubmitError(`Server returned unexpected status: ${response.status}`);
      }
    } catch (error) {
      console.error('API Error:', error);
      
      // If the API fails, try a fallback endpoint or show appropriate error
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        setSubmitError('Network error. Please check your connection and try again.');
      } else if (error.response) {
        // Server responded with error status
        setSubmitError(`Server error: ${error.response.status} - ${error.response.data?.message || 'Please try again later.'}`);
      } else if (error.request) {
        // Request made but no response received
        setSubmitError('No response from server. Please try again later.');
      } else {
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-block bg-gradient-to-r from-[#02ca02] to-[#02ca02]/80 text-white text-sm font-semibold py-2 px-6 rounded-full mb-6 shadow-md">
            Learn from Industry Experts
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Master <span className="text-[#02ca02]">Financial</span> Markets
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Comprehensive courses designed by experts to help you succeed in today's dynamic financial markets
          </p>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#02ca02]/10 rounded-full mb-4">
              <FaUsers className="text-2xl text-[#02ca02]" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-2">7,500+</h3>
            <p className="text-gray-600">Students Enrolled</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#02ca02]/10 rounded-full mb-4">
              <FaStar className="text-2xl text-[#02ca02]" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-2">4.8/5</h3>
            <p className="text-gray-600">Average Rating</p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#02ca02]/10 rounded-full mb-4">
              <FaChartLine className="text-2xl text-[#02ca02]" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-2">95%</h3>
            <p className="text-gray-600">Satisfaction Rate</p>
          </div>
        </div>

        {/* Tabbed Category Navigation */}
<div className="mb-14">
  <div className="relative">
    {/* Desktop: Elegant tab design */}
    <div className="hidden md:flex justify-center border-b border-gray-200">
      <div className="flex space-x-8">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`relative flex items-center gap-2 px-1 py-4 transition-all duration-200 group ${
              activeCategory === category.id
                ? "text-[#02ca02] font-medium"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <category.icon className="w-5 h-5 flex-shrink-0" />
            <span>{category.name}</span>
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
              activeCategory === category.id
                ? "bg-[#02ca02]/10 text-[#02ca02]"
                : "bg-gray-100 text-gray-600 group-hover:bg-gray-200"
            }`}>
              {category.count}
            </span>
            
            {/* Active indicator */}
            {activeCategory === category.id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#02ca02] rounded-t-full"></div>
            )}
          </button>
        ))}
      </div>
    </div>

    {/* Mobile & Tablet: Scrollable pills */}
    <div className="flex md:hidden overflow-x-auto pb-3 hide-scrollbar">
      <div className="flex space-x-2 min-w-max p-1 bg-gray-100 rounded-xl">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap ${
              activeCategory === category.id
                ? "bg-white text-[#02ca02] font-medium shadow-sm"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <category.icon className="w-4 h-4 flex-shrink-0" />
            <span>{category.name}</span>
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
              activeCategory === category.id
                ? "bg-[#02ca02]/10 text-[#02ca02]"
                : "bg-white text-gray-600"
            }`}>
              {category.count}
            </span>
          </button>
        ))}
      </div>
    </div>
    
    {/* Fade effect for mobile only */}
    <div className="absolute top-0 right-0 w-10 h-full bg-gradient-to-l from-gray-50 to-transparent pointer-events-none md:hidden"></div>
  </div>
</div>

        {/* Courses grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCourses.map(course => (
            <div key={course.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 border border-gray-100 group">
              <div className="h-52 overflow-hidden relative">
                <img src={course.image} alt={course.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute top-4 right-4 bg-[#02ca02] text-white text-xs font-bold py-1 px-3 rounded-full">
                  {course.level}
                </div>
                
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#02ca02] transition-colors">{course.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-2">{course.description}</p>
                
               <div className="mb-4">
  {/* Rating and Duration in a row */}
  <div className="flex items-center gap-3 mb-3">
    <div className="flex items-center bg-yellow-50 text-yellow-700 py-1 px-3 rounded-full">
      <FaStar className="text-yellow-500 mr-1 text-sm" />
      <span className="text-sm font-semibold">{course.rating}</span>
    </div>
    
    <div className="flex items-center bg-gray-100 text-gray-700 py-1 px-3 rounded-full">
      <FaClock className="text-[#02ca02] mr-1 text-sm" />
      <span className="text-sm">{course.duration}</span>
    </div>
  </div>
</div>
                
                
                <button
                  onClick={() => handleEnrollClick(course)}
                  className="w-full bg-gradient-to-r from-[#02ca02] to-[#02ca02]/80 text-white py-3 px-4 rounded-xl hover:from-[#02ca02] hover:to-[#02ca02]/90 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 flex items-center justify-center group/btn"
                >
                  Enroll Now
                  <svg className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedCourse && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleCloseModal}></div>

            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-auto p-6 sm:p-8 transform transition-all">
              <button
                onClick={handleCloseModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <FaTimes />
              </button>

              {!isSubmitted ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Enroll in <span className='text-[#02ca02]'>{selectedCourse.title}</span></h2>
                  <p className="text-gray-600 mb-6">Please provide your details to complete enrollment</p>
                  
                  {submitError && (
                    <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm border border-red-200">
                      {submitError}
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-xl border ${errors.name ? 'border-[#fe0000]' : 'border-gray-300'} shadow-sm px-4 py-3 focus:border-[#02ca02] focus:ring-2 focus:ring-[#02ca02]/20 transition-colors`}
                        placeholder="Enter your full name"
                      />
                      {errors.name && <p className="mt-1 text-sm text-[#fe0000]">{errors.name}</p>}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-xl border ${errors.email ? 'border-[#fe0000]' : 'border-gray-300'} shadow-sm px-4 py-3 focus:border-[#02ca02] focus:ring-2 focus:ring-[#02ca02]/20 transition-colors`}
                        placeholder="Enter your email"
                      />
                      {errors.email && <p className="mt-1 text-sm text-[#fe0000]">{errors.email}</p>}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`mt-1 block w-full rounded-xl border ${errors.phone ? 'border-[#fe0000]' : 'border-gray-300'} shadow-sm px-4 py-3 focus:border-[#02ca02] focus:ring-2 focus:ring-[#02ca02]/20 transition-colors`}
                        placeholder="Enter your phone number"
                      />
                      {errors.phone && <p className="mt-1 text-sm text-[#fe0000]">{errors.phone}</p>}
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message (Optional)</label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows="3"
                        className="mt-1 block w-full rounded-xl border border-gray-300 shadow-sm px-4 py-3 focus:border-[#02ca02] focus:ring-2 focus:ring-[#02ca02]/20 transition-colors"
                        placeholder="Any questions or comments?"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-[#02ca02] to-[#02ca02]/80 text-white py-3 px-4 rounded-xl hover:from-[#02ca02] hover:to-[#02ca02]/90 transition-all flex items-center justify-center shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
                    >
                      {isSubmitting ? (
                        <>
                          <FaSpinner className="animate-spin mr-2" /> Submitting...
                        </>
                      ) : (
                        'Submit Enrollment'
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-[#02ca02]/10 rounded-full mb-6">
                    <FaCheckCircle className="text-[#02ca02] text-4xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Enrollment Successful!</h3>
                  <p className="text-gray-600 mb-6">Thank you for enrolling in <span className='text-[#02ca02]'>{selectedCourse.title}</span>. We'll contact you within 24 hours with course details.</p>
                  <button
                    onClick={handleCloseModal}
                    className="bg-gradient-to-r from-[#02ca02] to-[#02ca02]/80 text-white px-8 py-3 rounded-xl hover:from-[#02ca02] hover:to-[#02ca02]/90 transition-all shadow-md hover:shadow-lg"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursesPage;